import { LoaderFunction } from "@remix-run/node";
import { useLoaderData } from "@remix-run/react";
import clsx from "clsx";
import { Hit as AlgoliaHit, UiState } from "instantsearch.js/es/types";
import pick from "lodash/pick";
import qs from "qs";
import { useEffect } from "react";
import {
  getServerState,
  InstantSearchServerState,
  InstantSearchSSRProvider,
  useConfigure,
  useInfiniteHits,
  useInstantSearch,
  useRefinementList,
} from "react-instantsearch";
import { To } from "react-router-dom";
import { StaticRouter } from "react-router-dom/server";
import { renderToString } from "react-dom/server";
import { AlgoliaExpert, AlgoliaInsight, AlgoliaPage } from "~/@types";
import { TextCta } from "~/components/ctas";
import AllSearchResultsCard from "~/components/search/AllSearchResultsCard";
import InsightSearchCard from "~/components/search/InsightSearchCard";
import SearchFilters from "~/components/search/SearchFilters";
import { SearchResultsContainer } from "~/components/search/SearchResultsContainer";
import { pageView, track } from "~/components/utils/gtm.client";
import SchemaOrg from "~/components/utils/SchemaOrg";
import {
  SchemaOrgType,
  tbiOrganizationSchema,
} from "~/components/utils/schemaOrgUtils.server";
import { useSearchParams } from "~/components/utils/useSearchParams";
import { ALGOLIA_FACETS, ALGOLIA_INDICES, getEnv } from "~/config";
import * as routeUtils from "~/contentful/route-utils";
import RootProvider from "~/contexts/RootProvider";
import { AlgoliaProvider, AlgoliaSearchInput } from "../components/search";

type InsightHit = AlgoliaHit<AlgoliaInsight>;
type ExpertHit = AlgoliaHit<AlgoliaExpert>;
type PageHit = AlgoliaHit<AlgoliaPage>;
type AllHit = InsightHit | ExpertHit | PageHit;

type HitType = InsightHit | ExpertHit | PageHit | AllHit;

function assertIsInsightHits(hits: HitType[]): hits is InsightHit[] {
  return hits[0]?.contentType === "insight";
}

function assertIsExpertHits(hits: HitType[]): hits is ExpertHit[] {
  return hits[0]?.contentType === "expert";
}

function assertIsPageHits(hits: HitType[]): hits is PageHit[] {
  return hits[0]?.contentType === "page";
}

type LoaderData = {
  url: string;
  serverState?: InstantSearchServerState;
  meta: routeUtils.MetaInfo;
  schemaOrg: SchemaOrgType;
};

export const loader: LoaderFunction = async ({
  request,
  context,
}): Promise<LoaderData> => {
  const serverState = await getServerState(
    <StaticRouter location={request.url}>
      <RootProvider>
        <SearchWrapper
          url={request.url}
          ip={context.ip as string | undefined}
        />
      </RootProvider>
    </StaticRouter>,
    { renderToString },
  );

  const schemaOrg: SchemaOrgType = {
    "@context": "https://schema.org",
    "@type": "SearchResultsPage",
    name: "Search TBI",
    publisher: tbiOrganizationSchema,
    url: `${getEnv("PUBLICLY_AVAILABLE_ORIGIN")}/search`,
  };

  return {
    serverState,
    url: request.url,
    meta: routeUtils.getMetaInfo({
      title: "Search Tony Blair Institute",
      description: "Search Tony Blair Institute",
      requestUrl: request.url,
    }),
    schemaOrg,
  };
};

const TAB_PARAM = "tab";
const tabs = {
  insights: "insights",
  experts: "experts",
  siteContent: "pages",
  all: "all",
} as const;

type TabValue = (typeof tabs)[keyof typeof tabs];

export function Search() {
  const instantSearch = useInstantSearch();
  const { hits, showMore, isLastPage, sendEvent } = useInfiniteHits<AllHit>();
  let currentTab = useSearchParams()[TAB_PARAM]?.[0] as TabValue;

  if (!Object.values(tabs).includes(currentTab as TabValue)) {
    // Use all tab if param is undefined or incorrect
    currentTab = tabs.insights;
  }
  // These hooks apply filters and sync with the url state
  useRefinementList({ attribute: ALGOLIA_FACETS.insights.topic });
  useRefinementList({ attribute: ALGOLIA_FACETS.insights.region });
  useRefinementList({ attribute: ALGOLIA_FACETS.insights.type });
  useRefinementList({ attribute: ALGOLIA_FACETS.insights.year });

  const hasSearchParams =
    Object.keys(pick(instantSearch.indexUiState, "query", "refinementList"))
      .length > 0;

  useConfigure({
    index: currentTab,
    analyticsTags: ["surface:search-page", `tab:${currentTab}`],
    // Avoids fetching results and reporting analytics when there are no search params
    ...(!hasSearchParams && { hitsPerPage: 0 }),
  });

  const onResultClick = (hit: HitType) => {
    // For Algolia analytics
    sendEvent("click", hit, "search result click");
  };

  const getTabLocation = (tab: TabValue): To => {
    const nextAlgoliaState: UiState = {
      [ALGOLIA_INDICES.all]: {
        query: instantSearch.uiState[ALGOLIA_INDICES.all].query,
      },
    };

    return {
      pathname: "/search",
      search: qs.stringify({ ...nextAlgoliaState, tab }),
    };
  };

  const renderResults = (tab: TabValue) => {
    if (tab === "all") {
      return (
        <>
          {hits.map((hit) =>
            hit.contentType === "insight" ? (
              <InsightSearchCard
                key={hit.objectID}
                insight={hit}
                onClick={() => onResultClick(hit)}
              />
            ) : (
              <AllSearchResultsCard
                key={hit.objectID}
                content={hit}
                onClick={() => onResultClick(hit)}
              />
            ),
          )}
        </>
      );
    }

    if (tab === tabs.insights && assertIsInsightHits(hits)) {
      return hits.map((hit) => (
        <InsightSearchCard
          key={hit.objectID}
          insight={hit}
          onClick={() => onResultClick(hit)}
        />
      ));
    }
    if (tab === tabs.experts && assertIsExpertHits(hits)) {
      return hits.map((hit) => (
        <AllSearchResultsCard
          key={hit.objectID}
          content={hit}
          onClick={() => onResultClick(hit)}
        />
      ));
    }
    if (tab === tabs.siteContent && assertIsPageHits(hits)) {
      return (
        <ul>
          {hits.map((hit) => (
            <AllSearchResultsCard
              key={hit.objectID}
              content={hit}
              onClick={() => onResultClick(hit)}
            />
          ))}
        </ul>
      );
    }
    // If a type assertion above fails, we'll end up here.
    // This will happen briefly when switching between tabs.
    // TODO: Add a loading message?
    return null;
  };

  useEffect(() => {
    track(
      pageView({
        category: "Search",
        searchTerm: instantSearch.results.query ?? "",
      }),
    );
    // track page view when new results come back
  }, [instantSearch.results.query]);

  return (
    <div className="flex w-full flex-col">
      <div className="below-nav flex-grow-0 bg-black ">
        <div className="global-container mt-2 w-full">
          <AlgoliaSearchInput preventDefault />
          <div className="mt-11 flex h-[52px] gap-x-6 overflow-x-scroll pb-7 xs:overflow-auto md:gap-x-9">
            <TextCta
              id="insights-search-tab-button"
              to={getTabLocation(tabs.insights)}
              kind="white-tertiary"
              type="button"
              label="Insights"
              underline={currentTab === tabs.insights}
            />
            <TextCta
              id="site-content-search-tab-button"
              to={getTabLocation(tabs.all)}
              kind="white-tertiary"
              type="button"
              label="All"
              underline={currentTab === tabs.all}
            />
            <TextCta
              id="site-content-search-tab-button"
              to={getTabLocation(tabs.siteContent)}
              kind="white-tertiary"
              type="button"
              label="Site Content"
              underline={currentTab === tabs.siteContent}
            />
            <TextCta
              id="experts-search-tab-button"
              to={getTabLocation(tabs.experts)}
              kind="white-tertiary"
              type="button"
              label="Experts"
              underline={currentTab === tabs.experts}
            />
            <SearchFilters
              className={clsx("ml-auto mr-0", {
                hidden: currentTab !== tabs.insights,
              })}
            />
          </div>
        </div>
      </div>
      {hasSearchParams && instantSearch.results.nbHits > 0 && (
        <SearchResultsContainer
          query={instantSearch.indexUiState.query}
          total={instantSearch.results.nbHits}
          hasMore={!isLastPage}
          onShowMoreClick={showMore}
        >
          {renderResults(currentTab)}
        </SearchResultsContainer>
      )}
      {hasSearchParams && instantSearch.results.nbHits === 0 && (
        <div className="flex-center h-80 flex-col ">
          <h3 className="max-w-md text-center">
            We couldn't find what you're looking for.
          </h3>
          <p className="eyebrows mt-8 normal-case text-tertiary_text">
            0 results for "{instantSearch.indexUiState.query}".
          </p>
        </div>
      )}
      {!hasSearchParams && (
        <div className="flex-center h-80">
          <h3 className="max-w-md text-center">
            Search or use filters to see results.
          </h3>
        </div>
      )}
    </div>
  );
}

export function SearchWrapper({
  url,
  serverState,
  ip,
}: Pick<LoaderData, "serverState" | "url"> & { ip?: string }) {
  return (
    <InstantSearchSSRProvider {...serverState}>
      <AlgoliaProvider
        url={url}
        indexName={ALGOLIA_INDICES.all}
        xForwardedFor={ip}
      >
        <Search />
      </AlgoliaProvider>
    </InstantSearchSSRProvider>
  );
}

export default function AlgoWrapper() {
  const { url, serverState, schemaOrg } = useLoaderData<LoaderData>();
  return (
    <>
      <SearchWrapper url={url} serverState={serverState} />
      <SchemaOrg content={schemaOrg} />
    </>
  );
}

export const shouldRevalidate = routeUtils.shouldRevalidate;
